import React, { useState, useCallback, useRef, useEffect } from "react";
import update from "immutability-helper";
import { Button, Modal, Flex, Text } from "@mantine/core";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";
import { IconArrowsMove, IconDragDrop } from "@tabler/icons-react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
const getEntityInfo = {
  ContestTierGroup: {
    name: "Tier Group",
    update_url: `/update-tier-group-positions/`,
    key_name: "contest_tier_groups",
  },
  ContestTier: {
    name: "Tier",
    update_url: `/update-tier-positions/`,
    key_name: "contest_tiers",
  },
};

export default function ContestTierReorder({
  items,
  entity,
  additionalReqData = {},
  onSuccess,
}) {
  const [draftItems, setDraftItems] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const entityInfo = getEntityInfo[entity];

  if (!entityInfo) return null;

  useEffect(() => {
    setDraftItems(items);
  }, [JSON.stringify(items)]);

  function onClose() {
    setOpen(false);
  }

  function onSaveClick() {
    setLoading(true);
    const req = {
      ...additionalReqData,
      [entityInfo.key_name]: draftItems.map((m, i) => ({
        id: m.id,
        position: i + 1,
      })),
    };

    axios
      .post(entityInfo.update_url, req)
      .then(() => {
        toast.success(t("common:updated") + "!");
        setLoading(false);
        onClose();
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setDraftItems((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  }, []);

  const renderCard = useCallback((card, index) => {
    return (
      <Card
        key={card.id}
        index={index}
        id={card.id}
        text={card.name}
        moveCard={moveCard}
      />
    );
  });

  return (
    <div>
      <Button
        leftSection={<IconArrowsMove />}
        onClick={() => setOpen(true)}
        color="teal"
        size="xs"
      >
        Reposition {entityInfo.name}s
      </Button>
      <Modal opened={isOpen} onClose={onClose}>
        <Text mb="lg" size="sm" align="center">
          Reposition {entityInfo.name}s by dragging{" "}
          <IconArrowsMove
            size={18}
            style={{ position: "relative", top: "5px" }}
          />{" "}
          and dropping{" "}
          <IconDragDrop
            size={18}
            style={{ position: "relative", top: "5px" }}
          />
        </Text>
        <DndProvider backend={HTML5Backend}>
          <div>{draftItems.map((card, i) => renderCard(card, i))}</div>
        </DndProvider>
        <Button loading={loading} mt="xl" fullWidth onClick={onSaveClick}>
          Save Order
        </Button>
      </Modal>
    </div>
  );
}

const Card = ({ id, text, index, moveCard }) => {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: "card",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: "card",
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));

  return (
    <StyledItem ref={ref} dragging={isDragging} data-handler-id={handlerId}>
      <Flex grow>
        <IconArrowsMove color="#000" />
        <Text ml="sm">{text}</Text>
      </Flex>
    </StyledItem>
  );
};

const StyledItem = styled.div`
  padding: 5px 3px;
  opacity: ${(props) => (props.dragging ? 0 : 1)};
  cursor: pointer;
  margin-bottom: 0.5rem;
  background-color: #fff;
  cursor: move;
  border: 1px dashed #eee;
`;
